import {
  Alert,
  Badge,
  Button,
  Checkbox,
  FileButton,
  Group,
  Modal,
  PasswordInput,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconCertificate, IconPlus, IconRefresh, IconUpload } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, izvedi, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { datum } from '../oblika/oblika';
import { Kartica, Podatki, StanjeNalaganja } from '../postavitev/Podatki';
import { barve } from '../tema';

type Prostor = Shema['PoslovniProstorPodatki'];
type Naprava = Shema['ElektronskaNapravaPodatki'];

/** »2026-10-07T10:15:00« → »7. 10. 2026 10:15«. */
const cas = (iso?: string | null) => (iso ? `${datum(iso.slice(0, 10))} ${iso.slice(11, 16)}` : '');

/**
 * FURS v Supermanu: certifikat za davčno potrjevanje, poslovni prostori (registracija pri FURS) in elektronske
 * naprave. Preverjanje podatkov, registracija in podpisovanje so na strežniku; tu se samo vnaša in prikazuje.
 */
export function FursNastavitve() {
  const [del, setDel] = useState<'certifikat' | 'prostori' | 'naprave'>('certifikat');
  return (
    <Stack gap="md">
      <SegmentedControl
        value={del}
        onChange={(v) => setDel(v as typeof del)}
        data={[
          { value: 'certifikat', label: 'Certifikat' },
          { value: 'prostori', label: 'Poslovni prostori' },
          { value: 'naprave', label: 'Naprave' },
        ]}
        w={{ base: '100%', xs: 'auto' }}
        style={{ alignSelf: 'flex-start' }}
      />
      {del === 'certifikat' && <Certifikat />}
      {del === 'prostori' && <PoslovniProstori />}
      {del === 'naprave' && <ElektronskeNaprave />}
    </Stack>
  );
}

function base64(datoteka: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(new NapakaApi('Datoteke ni bilo mogoče prebrati.', 'DATOTEKA', 0));
    r.readAsDataURL(datoteka);
  });
}

/** Naloženi certifikat (.p12/.pfx) in nalaganje novega z geslom; ključ ostane na strežniku. */
function Certifikat() {
  const odjemalec = useQueryClient();
  const certifikat = useQuery({
    queryKey: ['furs-certifikat'],
    // 204 = certifikat ni naložen
    queryFn: async ({ signal }) => (await podatki(api.GET('/api/furs/certifikat', { signal }))) ?? null,
  });
  const [datoteka, setDatoteka] = useState<File | null>(null);
  const [geslo, setGeslo] = useState('');
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);

  const nalozi = async () => {
    if (!datoteka) {
      setNapaka('Izberite datoteko certifikata (.p12 ali .pfx).');
      return;
    }
    setNapaka(null);
    setDela(true);
    try {
      const c = await podatki(api.PUT('/api/furs/certifikat', { body: { vsebina: await base64(datoteka), geslo } }));
      odjemalec.setQueryData(['furs-certifikat'], c);
      setDatoteka(null);
      setGeslo('');
      notifications.show({ color: 'green', message: 'Certifikat je naložen.' });
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDela(false);
    }
  };

  const c = certifikat.data;
  const potekel = !!c?.veljavenDo && c.veljavenDo < new Date().toISOString().slice(0, 10);
  return (
    <Stack gap="md">
      <Kartica naslov="Naloženi certifikat">
        {certifikat.isPending ? null : c ? (
          <>
            {potekel && (
              <Alert color="red" variant="light" mb="sm">
                Certifikat je potekel – računov z gotovinskim plačilom ni mogoče davčno potrditi.
              </Alert>
            )}
            <Podatki
              vrstice={[
                [
                  'Imetnik',
                  <span key="i" style={{ wordBreak: 'break-word' }}>
                    {c.imetnik}
                  </span>,
                ],
                [
                  'Izdajatelj',
                  <span key="z" style={{ wordBreak: 'break-word' }}>
                    {c.izdajatelj}
                  </span>,
                ],
                [
                  'Serijska št.',
                  <span key="s" style={{ wordBreak: 'break-all' }}>
                    {c.serijskaStevilka}
                  </span>,
                ],
                ['Velja do', datum(c.veljavenDo)],
              ]}
            />
          </>
        ) : (
          <Text size="sm" c="dimmed">
            Certifikat za davčno potrjevanje ni naložen.
          </Text>
        )}
      </Kartica>
      <Kartica naslov={c ? 'Zamenjaj certifikat' : 'Naloži certifikat'}>
        <Stack gap="sm">
          <Group gap="sm" align="center" wrap="wrap">
            <FileButton onChange={setDatoteka} accept=".p12,.pfx,application/x-pkcs12">
              {(p) => (
                <Button {...p} variant="default" leftSection={<IconCertificate size={18} />}>
                  Izberi datoteko
                </Button>
              )}
            </FileButton>
            <Text size="sm" c={datoteka ? undefined : 'dimmed'} style={{ wordBreak: 'break-all' }}>
              {datoteka?.name ?? 'Datoteka .p12 ali .pfx'}
            </Text>
          </Group>
          <PasswordInput
            label="Geslo certifikata"
            value={geslo}
            onChange={(e) => setGeslo(e.currentTarget.value)}
            maw={360}
          />
          {napaka && (
            <Alert color="red" variant="light" role="alert">
              {napaka}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button leftSection={<IconUpload size={18} />} onClick={() => void nalozi()} loading={dela}>
              Naloži
            </Button>
          </Group>
        </Stack>
      </Kartica>
    </Stack>
  );
}

const POLJA_PROSTORA: [keyof Prostor, string][] = [
  ['oznaka', 'Oznaka (npr. PE1)'],
  ['kataster', 'Katastrska občina'],
  ['stevilkaStavbe', 'Številka stavbe'],
  ['delStavbe', 'Del stavbe'],
  ['ulica', 'Ulica'],
  ['hisnaStevilka', 'Hišna št.'],
  ['hisnaStevilkaDodatek', 'Dodatek hišne št.'],
  ['naselje', 'Naselje'],
  ['postnaStevilka', 'Poštna št.'],
  ['kraj', 'Kraj / pošta'],
];

/**
 * Poslovni prostori – kot PoslovniProstoriOkno: seznam s stanjem registracije, obrazec z napakami po poljih med
 * vnosom (strežnik, …/preveri), shranjevanje tudi nepopolnega, »Sinhroniziraj s FURS«, zaprtje in brisanje.
 */
function PoslovniProstori() {
  const odjemalec = useQueryClient();
  const seznam = useQuery({
    queryKey: ['poslovni-prostori'],
    queryFn: ({ signal }) => podatki(api.GET('/api/poslovni-prostori', { signal })),
  });
  const [izbran, setIzbran] = useState<Prostor | null>(null);
  if (!seznam.data) {
    return (
      <StanjeNalaganja
        napaka={seznam.error}
        ponovi={() => void seznam.refetch()}
        naslov="Poslovnih prostorov ni bilo mogoče naložiti"
      />
    );
  }
  const osvezi = async (p?: Prostor) => {
    await odjemalec.invalidateQueries({ queryKey: ['poslovni-prostori'] });
    setIzbran(p ?? null);
  };
  return (
    <Stack gap="md">
      <Kartica
        naslov={`Poslovni prostori (${seznam.data.length})`}
        dejanja={
          <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={() => setIzbran({})}>
            Nov prostor
          </Button>
        }
      >
        {seznam.data.length === 0 ? (
          <Text size="sm" c="dimmed">
            Ni poslovnih prostorov.
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={480}>
            <Table highlightOnHover verticalSpacing={6}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Oznaka</Table.Th>
                  <Table.Th>Naslov</Table.Th>
                  <Table.Th>Stanje</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {seznam.data.map((p) => (
                  <Table.Tr
                    key={p.id}
                    onClick={() => setIzbran(p)}
                    style={{ cursor: 'pointer', background: p.id === izbran?.id ? barve.izbrano : undefined }}
                  >
                    <Table.Td fw={600}>{p.oznaka}</Table.Td>
                    <Table.Td>
                      {[[p.ulica, p.hisnaStevilka, p.hisnaStevilkaDodatek].filter(Boolean).join(' '), p.kraj]
                        .filter(Boolean)
                        .join(', ')}
                    </Table.Td>
                    <Table.Td>
                      <StanjeProstora p={p} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Kartica>
      {izbran && <ObrazecProstora key={izbran.id ?? 'nov'} shranjen={izbran} osvezi={osvezi} />}
    </Stack>
  );
}

function StanjeProstora({ p }: { p: Prostor }) {
  if (p.zaprt) {
    return (
      <Badge color="gray" variant="light">
        zaprt
      </Badge>
    );
  }
  if (p.sinhroniziran && !p.spremenjenPoSinhronizaciji) {
    return (
      <Badge color="green" variant="light">
        registriran
      </Badge>
    );
  }
  if (p.sinhroniziran) {
    return (
      <Badge color="yellow" variant="light">
        spremenjen
      </Badge>
    );
  }
  return (
    <Badge color={Object.keys(p.napake ?? {}).length ? 'red' : 'blue'} variant="light">
      {Object.keys(p.napake ?? {}).length ? 'nepopoln' : 'ni registriran'}
    </Badge>
  );
}

function ObrazecProstora({ shranjen, osvezi }: { shranjen: Prostor; osvezi: (p?: Prostor) => Promise<void> }) {
  const [p, setP] = useState<Prostor>(shranjen);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState<string | null>(null);
  const [zapiranje, setZapiranje] = useState(false);
  const [zakasnjen] = useDebouncedValue(p, 400);
  const preverjanje = useQuery({
    queryKey: ['preveri-prostor', zakasnjen],
    queryFn: ({ signal }) => podatki(api.POST('/api/poslovni-prostori/preveri', { signal, body: zakasnjen })),
  });
  const napake = preverjanje.data?.napake ?? shranjen.napake ?? {};
  const spremenjeno = JSON.stringify(p) !== JSON.stringify(shranjen);

  const izvedba = async (kdo: string, dejanje: () => Promise<Prostor | undefined>, sporociloUspeha: string) => {
    setNapaka(null);
    setDela(kdo);
    try {
      const nov = await dejanje();
      notifications.show({ color: 'green', message: sporociloUspeha });
      await osvezi(nov);
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDela(null);
    }
  };
  const shrani = () =>
    izvedba(
      'shrani',
      () =>
        p.id
          ? podatki(api.PUT('/api/poslovni-prostori/{id}', { params: { path: { id: p.id } }, body: p }))
          : podatki(api.POST('/api/poslovni-prostori', { body: p })),
      'Poslovni prostor je shranjen.',
    );
  const sinhroniziraj = () =>
    izvedba(
      'sinhronizacija',
      () => podatki(api.POST('/api/poslovni-prostori/{id}/sinhronizacija', { params: { path: { id: p.id! } } })),
      'Poslovni prostor je registriran pri FURS.',
    );
  const zapri = () =>
    izvedba(
      'zapri',
      async () => {
        const z = await podatki(api.POST('/api/poslovni-prostori/{id}/zaprtje', { params: { path: { id: p.id! } } }));
        setZapiranje(false);
        return z;
      },
      'Poslovni prostor je zaprt.',
    );
  const izbrisi = () =>
    izvedba(
      'izbrisi',
      async () => {
        await izvedi(api.DELETE('/api/poslovni-prostori/{id}', { params: { path: { id: p.id! } } }));
        return undefined;
      },
      'Poslovni prostor je izbrisan.',
    );

  return (
    <Kartica naslov={p.id ? `Poslovni prostor ${shranjen.oznaka ?? ''}` : 'Nov poslovni prostor'}>
      {shranjen.id && (
        <Text size="sm" c="dimmed" mb="sm">
          {shranjen.zaprt
            ? `Zaprt ${cas(shranjen.zaprt)}.`
            : shranjen.sinhroniziran
              ? `Registriran ${cas(shranjen.sinhroniziran)}${shranjen.sinhroniziral ? ` (${shranjen.sinhroniziral})` : ''}${shranjen.spremenjenPoSinhronizaciji ? ' – podatki so bili nato spremenjeni, znova sinhronizirajte.' : '.'}`
              : 'Še ni registriran pri FURS.'}
        </Text>
      )}
      {shranjen.napakaSinhronizacije && (
        <Alert color="red" variant="light" mb="sm" title="Zadnja registracija ni uspela">
          {shranjen.napakaSinhronizacije}
        </Alert>
      )}
      <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
        {POLJA_PROSTORA.map(([k, oznaka]) => (
          <TextInput
            key={k}
            label={oznaka}
            value={String(p[k] ?? '')}
            onChange={(e) => setP({ ...p, [k]: e.currentTarget.value })}
            error={napake[k]}
            disabled={k === 'oznaka' && shranjen.uporabljen}
            description={k === 'oznaka' && shranjen.uporabljen ? 'Prostor je že registriran ali na računu.' : undefined}
          />
        ))}
      </SimpleGrid>
      {napaka && (
        <Alert color="red" variant="light" mt="sm" role="alert">
          {napaka}
        </Alert>
      )}
      <Group justify="space-between" mt="md" gap="xs">
        <Group gap="xs">
          {shranjen.id && !shranjen.uporabljen && (
            <Button variant="subtle" color="red" onClick={() => void izbrisi()} loading={dela === 'izbrisi'}>
              Izbriši
            </Button>
          )}
          {shranjen.id && shranjen.sinhroniziran && !shranjen.zaprt && (
            <Button variant="subtle" color="red" onClick={() => setZapiranje(true)}>
              Zapri prostor
            </Button>
          )}
        </Group>
        <Group gap="xs">
          {shranjen.id && !shranjen.zaprt && (
            <Button
              variant="default"
              leftSection={<IconRefresh size={18} />}
              onClick={() => void sinhroniziraj()}
              loading={dela === 'sinhronizacija'}
              disabled={spremenjeno || !shranjen.lahkoSinhronizira}
              title={
                spremenjeno
                  ? 'Najprej shranite spremembe.'
                  : !shranjen.lahkoSinhronizira
                    ? 'Podatki niso popolni.'
                    : undefined
              }
            >
              Sinhroniziraj s FURS
            </Button>
          )}
          <Button onClick={() => void shrani()} loading={dela === 'shrani'} disabled={!!p.id && !spremenjeno}>
            {p.id ? 'Shrani' : 'Dodaj'}
          </Button>
        </Group>
      </Group>
      {shranjen.id && !shranjen.zaprt && (spremenjeno || !shranjen.lahkoSinhronizira) && (
        <Text size="xs" c="dimmed" ta="right" mt={4}>
          {spremenjeno ? 'Sinhronizacija: najprej shranite spremembe.' : 'Sinhronizacija: dopolnite označena polja.'}
        </Text>
      )}
      <Modal opened={zapiranje} onClose={() => setZapiranje(false)} title="Zapri poslovni prostor" radius="lg">
        <Text size="sm">
          Prostor {shranjen.oznaka} bo pri FURS zaprt in ga ni mogoče več uporabiti za nove račune. Želite nadaljevati?
        </Text>
        <Group justify="flex-end" mt="md" gap="xs">
          <Button variant="subtle" onClick={() => setZapiranje(false)}>
            Prekliči
          </Button>
          <Button color="red" onClick={() => void zapri()} loading={dela === 'zapri'}>
            Zapri prostor
          </Button>
        </Group>
      </Modal>
    </Kartica>
  );
}

/** Elektronske naprave (blagajne) – kot ElektronskeNapraveEditor: oznaka, opis, aktivna; brisanje neuporabljene. */
function ElektronskeNaprave() {
  const odjemalec = useQueryClient();
  const seznam = useQuery({
    queryKey: ['elektronske-naprave'],
    queryFn: ({ signal }) => podatki(api.GET('/api/elektronske-naprave', { signal })),
  });
  const [n, setN] = useState<Naprava>({ aktivna: true });
  const [polja, setPolja] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState<string | null>(null);
  if (!seznam.data) {
    return (
      <StanjeNalaganja
        napaka={seznam.error}
        ponovi={() => void seznam.refetch()}
        naslov="Naprav ni bilo mogoče naložiti"
      />
    );
  }
  const izvedba = async (kdo: string, dejanje: () => Promise<Naprava>, uspeh: string) => {
    setNapaka(null);
    setPolja({});
    setDela(kdo);
    try {
      setN(await dejanje());
      await odjemalec.invalidateQueries({ queryKey: ['elektronske-naprave'] });
      notifications.show({ color: 'green', message: uspeh });
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(null);
    }
  };
  const shrani = () =>
    izvedba(
      'shrani',
      () =>
        n.id
          ? podatki(api.PUT('/api/elektronske-naprave/{id}', { params: { path: { id: n.id } }, body: n }))
          : podatki(api.POST('/api/elektronske-naprave', { body: n })),
      'Naprava je shranjena.',
    );
  const izbrisi = () =>
    izvedba(
      'izbrisi',
      async () => {
        await izvedi(api.DELETE('/api/elektronske-naprave/{id}', { params: { path: { id: n.id! } } }));
        return { aktivna: true };
      },
      'Naprava je izbrisana.',
    );

  return (
    <Stack gap="md">
      <Kartica
        naslov={n.id ? `Naprava ${n.oznaka ?? ''}` : 'Nova naprava'}
        dejanja={
          n.id ? (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={() => setN({ aktivna: true })}
            >
              Nova
            </Button>
          ) : undefined
        }
      >
        <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
          <TextInput
            label="Oznaka (npr. BL1)"
            value={n.oznaka ?? ''}
            onChange={(e) => setN({ ...n, oznaka: e.currentTarget.value })}
            error={polja.oznaka}
            disabled={n.uporabljena}
            description={n.uporabljena ? 'Naprava je že na računih.' : undefined}
          />
          <TextInput
            label="Opis"
            value={n.opis ?? ''}
            onChange={(e) => setN({ ...n, opis: e.currentTarget.value })}
            error={polja.opis}
          />
        </SimpleGrid>
        <Checkbox
          mt="sm"
          label="Aktivna (izbira za nove račune)"
          checked={!!n.aktivna}
          onChange={(e) => setN({ ...n, aktivna: e.currentTarget.checked })}
        />
        {napaka && (
          <Alert color="red" variant="light" mt="sm" role="alert">
            {napaka}
          </Alert>
        )}
        <Group justify="flex-end" mt="md" gap="xs">
          {n.id && !n.uporabljena && (
            <Button variant="subtle" color="red" onClick={() => void izbrisi()} loading={dela === 'izbrisi'}>
              Izbriši
            </Button>
          )}
          <Button onClick={() => void shrani()} loading={dela === 'shrani'}>
            {n.id ? 'Shrani' : 'Dodaj'}
          </Button>
        </Group>
      </Kartica>
      <Kartica naslov={`Naprave (${seznam.data.length})`}>
        {seznam.data.length === 0 ? (
          <Text size="sm" c="dimmed">
            Ni naprav.
          </Text>
        ) : (
          <Table highlightOnHover verticalSpacing={6}>
            <Table.Tbody>
              {seznam.data.map((x) => (
                <Table.Tr
                  key={x.id}
                  onClick={() => setN(x)}
                  style={{ cursor: 'pointer', background: x.id === n.id ? barve.izbrano : undefined }}
                >
                  <Table.Td fw={600}>{x.oznaka}</Table.Td>
                  <Table.Td>{x.opis}</Table.Td>
                  <Table.Td ta="right">
                    {!x.aktivna && (
                      <Badge color="gray" variant="light">
                        neaktivna
                      </Badge>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Kartica>
    </Stack>
  );
}
